(function () {
  const KLAVIYO_REVISION = '2024-07-15';
  const LEGACY_SUBSCRIBE_URL = 'https://manage.kmail-lists.com/ajax/subscriptions/subscribe';
  const CLIENT_SUBSCRIBE_URL = 'https://a.klaviyo.com/client/subscriptions/';

  function getKlaviyoCompanyIdFromPage() {
    const scripts = document.querySelectorAll('script[src*="klaviyo"]');
    for (const script of scripts) {
      const match = script.src.match(/company_id=([^&]+)/i);
      if (match) return decodeURIComponent(match[1]).trim();
    }

    return '';
  }

  function normalizeId(value) {
    return (value || '').trim();
  }

  async function parseKlaviyoError(response) {
    const raw = await response.text();
    if (!raw) return '';

    try {
      const data = JSON.parse(raw);
      if (Array.isArray(data.errors) && data.errors.length) {
        return data.errors
          .map((entry) => entry.detail || entry.title || entry.message || entry)
          .filter(Boolean)
          .join(' ');
      }
      if (data.message) return String(data.message);
    } catch (_error) {
      return raw.slice(0, 180);
    }

    return '';
  }

  function buildClientPayload(email, listId, customSource) {
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: customSource,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId,
            },
          },
        },
      },
    };
  }

  async function subscribeViaClientApi(companyId, listId, email, customSource) {
    const response = await fetch(
      `${CLIENT_SUBSCRIBE_URL}?company_id=${encodeURIComponent(companyId)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify(buildClientPayload(email, listId, customSource)),
      }
    );

    if (response.status === 202 || response.ok) {
      return;
    }

    const detail = await parseKlaviyoError(response);
    throw new Error(detail || `Client API failed (${response.status}).`);
  }

  async function subscribeViaLegacyAjax(listId, email, customSource) {
    const params = new URLSearchParams({
      g: listId,
      email,
    });

    if (customSource) {
      params.set('$fields', '$source');
      params.set('$source', customSource);
    }

    const response = await fetch(LEGACY_SUBSCRIBE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Cache-Control': 'no-cache',
      },
      body: params.toString(),
    });

    const raw = await response.text();
    let data = {};

    try {
      data = raw ? JSON.parse(raw) : {};
    } catch (_error) {
      data = {};
    }

    if (response.ok && data.success !== false) {
      return;
    }

    const detail =
      (Array.isArray(data.errors) && data.errors.join(' ')) ||
      data.message ||
      (raw ? raw.slice(0, 180) : '');

    throw new Error(detail || `Legacy subscribe failed (${response.status}).`);
  }

  async function subscribeToKlaviyo(form, email) {
    const listId = normalizeId(form.dataset.klaviyoListId);
    const customSource = form.dataset.klaviyoSource || 'Hero Opt-In';
    let companyId = normalizeId(form.dataset.klaviyoCompanyId) || getKlaviyoCompanyIdFromPage();

    if (!listId) {
      throw new Error('Klaviyo list ID is not configured in the theme editor.');
    }

    const attempts = [];

    if (companyId) {
      try {
        await subscribeViaClientApi(companyId, listId, email, customSource);
        return;
      } catch (error) {
        attempts.push(error instanceof Error ? error.message : String(error));
      }
    }

    try {
      await subscribeViaLegacyAjax(listId, email, customSource);
      return;
    } catch (error) {
      attempts.push(error instanceof Error ? error.message : String(error));
    }

    const message = attempts.find(Boolean);
    if (message && /invalid company id/i.test(message)) {
      throw new Error(
        'Invalid Klaviyo Company ID. Use the 6-character Site ID from your Klaviyo embed script, not a private API key.'
      );
    }

    throw new Error(message || 'Subscription failed. Please try again.');
  }

  function trackLeadEvent(form, email) {
    const source = form.dataset.klaviyoSource || 'Hero Opt-In';
    const emailNormalized = (email || '').trim().toLowerCase();

    if (typeof fbq === 'function') {
      fbq('track', 'Lead', {
        content_name: source,
        email: emailNormalized,
      });
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'lead',
      lead_source: source,
      email: emailNormalized,
    });

    if (typeof Shopify !== 'undefined' && Shopify.analytics?.publish) {
      try {
        Shopify.analytics.publish('lead', { source, email: emailNormalized });
      } catch (_error) {
        /* analytics optional */
      }
    }

    document.dispatchEvent(
      new CustomEvent('anna-chu:lead', {
        bubbles: true,
        detail: { source, email: emailNormalized },
      })
    );
  }

  function showSuccess(form) {
    const input = form.querySelector('[data-hero-optin-input]');
    const actions = form.querySelector('[data-hero-optin-actions]');
    const caption = form.querySelector('[data-hero-optin-caption]');
    const error = form.querySelector('[data-hero-optin-error]');
    const successText = form.dataset.successInput || 'Thank you!';
    const successCaption = form.dataset.successCaption || 'Please check your inbox for the email from us';

    if (error) {
      error.hidden = true;
      error.textContent = '';
    }

    if (input) {
      input.value = successText;
      input.placeholder = '';
      input.disabled = true;
      input.setAttribute('aria-readonly', 'true');
    }

    if (actions) {
      actions.hidden = true;
    }

    if (caption) {
      caption.textContent = successCaption;
    }

    form.classList.add('is-success');
  }

  function showError(form, message) {
    const error = form.querySelector('[data-hero-optin-error]');
    if (!error) return;

    error.textContent = message;
    error.hidden = false;
  }

  function setLoading(form, isLoading) {
    form.classList.toggle('is-loading', isLoading);
    form.querySelectorAll('button[type="submit"]').forEach((button) => {
      button.disabled = isLoading;
    });
  }

  function initHeroOptin(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const forms = scope.matches?.('[data-hero-optin-form]')
      ? [scope]
      : scope.querySelectorAll('[data-hero-optin-form]');

    forms.forEach((form) => {
      if (!(form instanceof HTMLFormElement) || form.dataset.heroOptinInit === 'true') return;
      form.dataset.heroOptinInit = 'true';

      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        const input = form.querySelector('[data-hero-optin-input]');
        if (!input || form.classList.contains('is-success')) return;

        const email = input.value.trim();
        if (!email) return;

        setLoading(form, true);

        try {
          await subscribeToKlaviyo(form, email);
          trackLeadEvent(form, email);
          showSuccess(form);
        } catch (error) {
          showError(form, error instanceof Error ? error.message : 'Something went wrong. Please try again.');
        } finally {
          setLoading(form, false);
        }
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => initHeroOptin(document));
  document.addEventListener('shopify:section:load', (event) => initHeroOptin(event.target));
})();
